import type { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'

import { STUDIO_FACTS } from '@/constants/studio'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const PEAK = STUDIO_FACTS.daylightPeak

/**
 * Share of each hour above 300 lux at the desk, averaged over a year of readings: bright around
 * midday and through the summer months, dim in midwinter. Deterministic, so server and client agree.
 */
const buildGrid = (): Array<Array<number>> => {
    const raw = MONTHS.map((_, month) => {
        const season = 0.5 + 0.5 * Math.cos((2 * Math.PI * (month - 5.5)) / 12)
        const half = 3.2 + 5.6 * season

        return HOURS.map(hour => {
            const offset = Math.abs(hour + 0.5 - 12.5)

            if (offset >= half) return 0

            const shape = Math.pow(Math.cos((offset / half) * (Math.PI / 2)), 1.6)

            return shape * (0.22 + 0.78 * season)
        })
    })

    const max = Math.max(...raw.flat())

    return raw.map(row => row.map(value => Math.round((value / max) * PEAK)))
}

const GRID = buildGrid()

/**
 * Dark to cream for low and middle values, turning signal blue at the bright end, the one
 * accent colour the site uses.
 */
const cellColor = (cell: number): string => {
    if (cell <= 0) return 'rgba(236, 230, 216, 0.035)'

    const t = Math.min(1, cell / PEAK)
    const alpha = 0.06 + 0.9 * Math.pow(t, 1.15)
    const warm = Math.max(0, (t - 0.55) / 0.45)
    const r = Math.round(236 + (134 - 236) * warm)
    const g = Math.round(230 + (182 - 230) * warm)
    const b = Math.round(216 + (240 - 216) * warm)

    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`
}

const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

interface DaylightHeatmapProps {
    title: string
    className?: string
}

/**
 * 24 hours by 12 months. Cells fade in along a diagonal ripple when the parent block reveals.
 * Hover or arrow keys read any cell in the readout above the grid.
 */
export const DaylightHeatmap: FC<DaylightHeatmapProps> = ({ title, className }): ReactNode => {
    const [active, setActive] = useState<{ month: number, hour: number } | null>(null)
    const shown = active ?? { month: 5, hour: 12 }
    const value = GRID[shown.month][shown.hour]

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const moves: Record<string, [number, number]> = {
            ArrowLeft: [0, -1],
            ArrowRight: [0, 1],
            ArrowUp: [-1, 0],
            ArrowDown: [1, 0]
        }
        const move = moves[event.key]

        if (!move) {
            if (event.key === 'Escape') setActive(null)

            return
        }

        event.preventDefault()
        event.stopPropagation()

        setActive(current => {
            const base = current ?? { month: 5, hour: 12 }

            return {
                month: Math.min(11, Math.max(0, base.month + move[0])),
                hour: Math.min(23, Math.max(0, base.hour + move[1]))
            }
        })
    }

    return (
        <div className={cn('w-full', className)}>
            <div className='mb-4 flex items-baseline justify-between gap-4' aria-live='polite'>
                <p className='hud text-mute'>
                    {active ? 'Readout' : 'Peak'}
                </p>

                <p className='hud text-ink'>
                    {MONTHS[shown.month]} / {hourLabel(shown.hour)} / {value}%
                </p>
            </div>

            <div
                tabIndex={0}
                role='group'
                aria-label={`${title}. Use the arrow keys to read each hour and month.`}
                onKeyDown={onKeyDown}
                onBlur={() => setActive(null)}
                onPointerLeave={() => setActive(null)}
                className='grid select-none gap-px'
                style={{ gridTemplateColumns: '28px repeat(24, minmax(0, 1fr))' }}
            >
                {GRID.map((row, month) => (
                    <div key={MONTHS[month]} className='contents'>
                        <span className='flex items-center font-mono text-[10px] uppercase tracking-[0.08em] text-mute' aria-hidden='true'>
                            {MONTHS[month].slice(0, 1)}
                        </span>

                        {row.map((cell, hour) => {
                            const isPeak = month === 5 && hour === 12
                            const isActive = active?.month === month && active.hour === hour
                            const style: CSSProperties = {
                                backgroundColor: cellColor(cell),
                                '--delay': `${(month + hour) * 22}ms`
                            } as CSSProperties

                            return (
                                <span
                                    key={hour}
                                    aria-hidden='true'
                                    onPointerEnter={() => setActive({ month, hour })}
                                    className={cn(
                                        'reveal-cell relative h-3 sm:h-3.5',
                                        isPeak && !isActive && 'outline outline-1 -outline-offset-1 outline-signal',
                                        isActive && 'outline outline-1 -outline-offset-1 outline-ink'
                                    )}
                                    style={style}
                                />
                            )
                        })}
                    </div>
                ))}

                <span aria-hidden='true' />

                {HOURS.map(hour => (
                    <span key={hour} className='pt-2 font-mono text-[10px] tracking-[0.06em] text-mute' aria-hidden='true'>
                        {hour % 6 === 0 ? String(hour).padStart(2, '0') : ''}
                    </span>
                ))}
            </div>

            <div className='mt-5 flex items-center gap-3' aria-hidden='true'>
                <span className='hud text-mute'>
                    0%
                </span>

                <span className='flex gap-px'>
                    {[0.08, 0.25, 0.42, 0.6, 0.8, 1].map(step => (
                        <span key={step} className='h-2 w-4' style={{ backgroundColor: cellColor(step * PEAK) }} />
                    ))}
                </span>

                <span className='hud text-mute'>
                    {PEAK}%
                </span>

                <span className='ml-auto flex items-center gap-2 hud text-mute'>
                    <span className='inline-block size-2.5 outline outline-1 -outline-offset-1 outline-signal' />
                    Noon peak
                </span>
            </div>

            <table className='sr-only'>
                <caption>
                    {title}. Values are the share of each hour above 300 lux.
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>
                            Month
                        </th>

                        {HOURS.map(hour => (
                            <th key={hour} scope='col'>
                                {hourLabel(hour)}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {GRID.map((row, month) => (
                        <tr key={MONTHS[month]}>
                            <th scope='row'>
                                {MONTHS[month]}
                            </th>

                            {row.map((cell, hour) => (
                                <td key={hour}>
                                    {cell}%
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
